import React from 'react';
import { Container, Typography, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const CopyrightFairUse: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/blog')}
          sx={{ mb: 4, color: 'text.secondary' }}
        >
          {t('blog.backToBlog')}
        </Button>

        <Typography variant="overline" color="primary" sx={{ fontWeight: 600, letterSpacing: 2, display: 'block', mb: 2 }}>
          Creator Tips • August 2, 2026
        </Typography>

        <Typography variant="h3" component="h1" sx={{ mb: 3, fontWeight: 700 }}>
          How to Handle Copyright and Fair Use
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.85, fontSize: '1.1rem' }}>
          Copyright strikes can destroy your channel overnight. Understanding copyright law and fair use isn't optional—it's essential for every creator on IndeeClips. This guide will help you navigate copyright safely, protect your own content, and understand when you can legally use others' work without getting into trouble.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          What Is Copyright? (The Basics Every Creator Needs)
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Copyright is automatic legal protection that applies the moment someone creates an original work—whether it's a video, song, photo, article, or software. In India, copyright lasts for the creator's lifetime plus 60 years. You don't need to register it; it exists automatically.
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
          This means every video you upload to IndeeClips is automatically YOUR copyrighted work. But it also means that music you didn't create, movie clips you didn't film, and photos you didn't take belong to someone else—and using them without permission can get you in serious trouble.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          What You CANNOT Do (Avoid These Copyright Mistakes)
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          1. Using Copyrighted Music Without Permission
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          This is the #1 copyright mistake creators make. You cannot use commercial songs in your videos without proper licensing—even if you're not monetizing, even if it's just 10 seconds, even if you give credit.
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>DON'T:</strong> Use Bollywood songs, Hollywood soundtracks, or popular music from Spotify in your videos</li>
          <li><strong>DON'T:</strong> Think "No Copyright Intended" in the description protects you (it doesn't)</li>
          <li><strong>DO:</strong> Use royalty-free music from YouTube Audio Library, Epidemic Sound, or Artlist</li>
          <li><strong>DO:</strong> Look for Creative Commons music on Free Music Archive or Incompetech</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          2. Re-uploading Others' Videos
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Downloading someone else's video and re-uploading it (even with commentary or reactions added) is copyright infringement unless you have explicit permission or it qualifies as fair use.
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>DON'T:</strong> Download Instagram reels, TikToks, or YouTube videos and repost them as compilations</li>
          <li><strong>DON'T:</strong> Assume giving credit makes it legal (it doesn't—you need permission)</li>
          <li><strong>DO:</strong> Ask creators for permission via email or DM before featuring their content</li>
          <li><strong>DO:</strong> Use Creative Commons licensed videos with proper attribution</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          3. Using Movie/TV Show Clips
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Movie studios aggressively protect their content. Using clips from films or TV shows is risky unless you're doing transformative commentary or criticism that qualifies as fair use (more on this below).
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Understanding Fair Use in India
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          India doesn't have "Fair Use" like the US—we have "Fair Dealing" under Section 52 of the Copyright Act. It's narrower and more restrictive. Fair Dealing allows copyrighted material to be used WITHOUT permission only for specific purposes:
        </Typography>

        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Private or personal use</strong> (watching a movie at home)</li>
          <li><strong>Criticism or review</strong> (analyzing a film scene)</li>
          <li><strong>Reporting current events</strong> (news coverage)</li>
          <li><strong>Research or education</strong> (teaching purposes)</li>
          <li><strong>Parody or satire</strong> (making fun of the original work)</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          The Four-Factor Test for Fair Dealing
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Courts consider these factors when deciding if your use qualifies as fair dealing:
        </Typography>

        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Purpose and character:</strong> Is it transformative? Are you adding commentary, criticism, or creating something new? Educational/non-profit use is stronger than commercial use.</li>
          <li><strong>Nature of the copyrighted work:</strong> Using factual works (news, documentaries) is more defensible than creative works (movies, songs).</li>
          <li><strong>Amount used:</strong> Using 30 seconds from a 2-hour movie is better than using 5 minutes. Use only what's necessary to make your point.</li>
          <li><strong>Effect on market value:</strong> Does your use hurt the original creator's ability to profit? If your video replaces the need to watch theirs, it's NOT fair use.</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Examples of Likely Fair Dealing</Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Film review:</strong> Showing 2-3 short clips (10-20 seconds each) while analyzing cinematography, acting, or storytelling</li>
          <li><strong>Educational tutorial:</strong> Using brief software screenshots to teach how the tool works</li>
          <li><strong>News commentary:</strong> Showing viral video clips while discussing the news event</li>
          <li><strong>Parody/satire:</strong> Creating a comedic version of a popular song or video format</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Examples of NOT Fair Dealing</Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Reaction videos:</strong> Just watching someone's video with your face in the corner adds minimal transformative value</li>
          <li><strong>Compilation videos:</strong> "Best TikToks of 2026" is just re-uploading others' work</li>
          <li><strong>Background music:</strong> Playing a full copyrighted song in your vlog background isn't transformative</li>
          <li><strong>Extended clips:</strong> Showing 10-minute scenes from a movie, even with commentary, uses too much of the original</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          How to Use Copyrighted Content Safely
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          1. Get Permission (Licensing)
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          The safest approach is always to ask for permission. Email the creator, explain how you'll use their content, and get written approval. Many creators are happy to say yes if you're promoting their work.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          2. Use Creative Commons Content
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Creative Commons licenses allow you to use content legally if you follow the license terms (usually attribution). Find CC-licensed content on:
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Unsplash, Pexels, Pixabay (photos and videos)</li>
          <li>Free Music Archive, Incompetech (music)</li>
          <li>YouTube Audio Library (music and sound effects)</li>
          <li>Wikimedia Commons (images and media)</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          3. Create Original Content
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          The absolute safest strategy: create everything yourself or hire people to create for you. Film your own footage, record your own music, take your own photos. Zero copyright risk.
        </Typography>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          What Happens If You Get a Copyright Strike?
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          IndeeClips follows a three-strike system similar to other platforms:
        </Typography>

        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>First strike:</strong> Warning. The infringing video is removed. You may lose some privileges temporarily.</li>
          <li><strong>Second strike:</strong> Serious warning. May result in temporary suspension (2-4 weeks).</li>
          <li><strong>Third strike:</strong> Channel termination. All your videos deleted, and you may be banned from creating new channels.</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          How to Dispute a Copyright Strike
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          If you believe a strike was issued in error (you had permission, or it was fair dealing):
        </Typography>

        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Submit a counter-notification</strong> through IndeeClips' DMCA process</li>
          <li><strong>Provide evidence:</strong> Permission emails, fair use justification, or proof of original creation</li>
          <li><strong>Be professional:</strong> Angry, defensive responses rarely help your case</li>
          <li><strong>Consult a lawyer</strong> if the content is critical to your channel and the strike is unjustified</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Protecting YOUR Content from Copyright Theft
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 2, lineHeight: 1.85 }}>
          Now that you understand copyright, protect your own work:
        </Typography>

        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>Watermark your videos:</strong> Subtle logo in the corner deters re-uploaders</li>
          <li><strong>Monitor for theft:</strong> Search for your video titles on IndeeClips and Google periodically</li>
          <li><strong>File DMCA takedowns:</strong> If someone steals your content, use IndeeClips' copyright reporting tool</li>
          <li><strong>Register important works:</strong> For high-value content, consider registering copyright with the Indian Copyright Office for stronger legal protection</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Copyright Myths Debunked
        </Typography>

        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li><strong>MYTH:</strong> "If I give credit, I can use it." <strong>REALITY:</strong> Credit doesn't grant permission. You still need a license or fair use justification.</li>
          <li><strong>MYTH:</strong> "If there's no copyright symbol ©, it's not protected." <strong>REALITY:</strong> Copyright is automatic. The © symbol is optional.</li>
          <li><strong>MYTH:</strong> "It's online, so it's public domain." <strong>REALITY:</strong> Public domain means copyright has expired (usually 60+ years after creator's death). Most online content is copyrighted.</li>
          <li><strong>MYTH:</strong> "I'm not monetizing, so it's legal." <strong>REALITY:</strong> Commercial use is just one factor. Non-profit use doesn't automatically equal fair use.</li>
          <li><strong>MYTH:</strong> "I changed 10% of it, so it's original." <strong>REALITY:</strong> Derivative works (modified versions) still infringe copyright unless substantially transformative.</li>
        </Box>

        <Typography variant="h5" sx={{ mt: 6, mb: 2, fontWeight: 700 }}>
          Resources for Copyright-Free Content
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Music
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>YouTube Audio Library (free)</li>
          <li>Epidemic Sound (₹1,200/month, huge library)</li>
          <li>Artlist (₹15,000/year, unlimited downloads)</li>
          <li>Free Music Archive (free Creative Commons music)</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Video Footage
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Pexels Videos (free HD stock footage)</li>
          <li>Pixabay (free videos and images)</li>
          <li>Coverr (free background videos)</li>
          <li>Videvo (free and paid stock footage)</li>
        </Box>

        <Typography variant="h6" sx={{ mt: 4, mb: 2, fontWeight: 600 }}>
          Images/Photos
        </Typography>
        <Box component="ul" sx={{ '& li': { mb: 1.5, color: 'text.secondary', lineHeight: 1.85 } }}>
          <li>Unsplash (free high-quality photos)</li>
          <li>Pexels (free stock photos)</li>
          <li>Pixabay (free images)</li>
          <li>Wikimedia Commons (free media files)</li>
        </Box>

        <Box
          sx={{
            mt: 6,
            p: 4,
            bgcolor: 'background.paper',
            borderRadius: 2,
            border: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            When in Doubt, Don't Use It
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.85 }}>
            Copyright law is complex, and this guide provides general information—not legal advice. If you're unsure whether your use qualifies as fair dealing, err on the side of caution. One copyright strike can undo months of hard work building your channel.
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.85 }}>
            Create original content whenever possible, use licensed or Creative Commons materials, and always add substantial value when referencing others' work. Your channel's longevity depends on it. For copyright questions specific to IndeeClips, contact{' '}
            <Typography
              component="a"
              href="mailto:contact@indeeclips.com"
              sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
            >
              contact@indeeclips.com
            </Typography>
            .
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default CopyrightFairUse;
